import { fg } from '@atlaskit/platform-feature-flags';
import type {
	ClientId,
	CommandConstructor,
	CommandProperties,
	CommandResult,
	RovoContentBridgeAPI,
} from '@atlassian/rovo-content-bridge-api';
import {
	ConfluencePageCreateCommand,
	ConfluenceUpdatePageCommand,
	ReadContentCommand,
	RovoStreamAbortedCommand,
} from '@atlassian/rovo-content-bridge-api-commands';

import { AbortCommandHandler } from './command-handlers/abort-command-handler';
import type { ICommandHandler, PassThroughCommandProps } from './command-handlers/command-handler';
import { CreateCommandHandler } from './command-handlers/create-command-handler';
import { ReadContentCommandHandler } from './command-handlers/read-content-command-handler';
import { StreamingCommandHandler } from './command-handlers/streaming-command-handler';
import { UpdateCommandHandler } from './command-handlers/update-command-handler';
import { DocumentCreateCommandProcessor } from './stream-processors/create-command-processor';
import { DocumentUpdateCommandProcessor } from './stream-processors/update-command-processor';

export class CommandManager {
	private commandHandlers: Map<
		CommandConstructor<CommandProperties, CommandResult>,
		ICommandHandler<CommandProperties, CommandResult>
	> = new Map();
	public readonly createCommandProcessor: DocumentCreateCommandProcessor;
	public readonly updateCommandProcessor: DocumentUpdateCommandProcessor;

	constructor(
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
		public readonly commandProps: PassThroughCommandProps,
	) {
		this.createCommandProcessor = new DocumentCreateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
		);
		this.updateCommandProcessor = new DocumentUpdateCommandProcessor(
			this.commandProps.editorAPI,
			this.commandProps.contentLocation,
			this.clientId,
			this.rovoBridgeAPI,
		);
	}

	addCommandHandler<
		P extends CommandProperties = CommandProperties,
		R extends CommandResult = CommandResult,
	>(command: CommandConstructor<P, R>, handler: ICommandHandler<P, R>): CommandManager {
		this.commandHandlers.set(
			command,
			handler as unknown as ICommandHandler<CommandProperties, CommandResult>,
		);
		return this;
	}

	addDefaultCommandHandlers(): CommandManager {
		this.addCreateCommandHandler();
		this.addUpdateCommandHandler();
		this.addReadCommandHandler();
		this.addAbortCommandHandler();
		return this;
	}

	registerCommands() {
		this.commandHandlers.forEach((handler, command) => {
			this.rovoBridgeAPI.on(this.clientId, command, (payload) => {
				return handler.execute(payload);
			});
		});
	}

	deregisterCommands() {
		this.commandHandlers.forEach((_, command) => {
			this.rovoBridgeAPI.off(this.clientId, command);
		});
		this.commandHandlers.clear();
	}

	private addCreateCommandHandler() {
		const createCommandHandler = new CreateCommandHandler(
			this.commandProps,
			this.createCommandProcessor,
		);
		this.addCommandHandler(ConfluencePageCreateCommand, createCommandHandler);
	}

	private addUpdateCommandHandler() {
		const updateCommandHandler = new UpdateCommandHandler(
			this.commandProps,
			this.updateCommandProcessor,
		);
		this.addCommandHandler(ConfluenceUpdatePageCommand, updateCommandHandler);
	}

	private addReadCommandHandler() {
		const readCommandHandler = new ReadContentCommandHandler(this.commandProps);
		this.addCommandHandler(ReadContentCommand, readCommandHandler);
	}

	private addAbortCommandHandler() {
		const abortCommandHandler = new AbortCommandHandler(this.commandProps, () => {
			this.commandHandlers.forEach((handler) => {
				if (handler instanceof StreamingCommandHandler) {
					if (fg('platform_editor_ai_aifc_patch_beta_2')) {
						handler.handleAbort();
					} else {
						handler.abort();
					}
				}
			});
			this.commandProps.onStreamAbort?.();
		});
		this.addCommandHandler(RovoStreamAbortedCommand, abortCommandHandler);
	}
}
